import { useEffect, useRef, useState } from 'react';
import { useStore } from '@/store/useStore';
import { Modal } from '../ui/Modal';
import { PALETTE, type Branch } from '@/types';
import { Trash2, Send, Check, Copy, Loader2, X, Link2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { tzOptionsWith } from '@/utils/timezones';
import {
  BranchHasDealsError, deleteBranch as apiDeleteBranch,
  requestBranchTelegramCode, unlinkBranchTelegram, type TelegramCode,
} from '@/data/branchesClient';

interface Props { id: string | null; onClose: () => void; }

/**
 * Карточка филиала. Новые филиалы здесь НЕ создаются — только на экране «Филиалы»
 * кнопкой «Подключить» у города из Битрикса: филиал, заведённый вручную, не связан
 * с городом в CRM, и сделки к нему никогда не приедут.
 */
export function BranchEditor({ id, onClose }: Props) {
  const { branches, saveBranch, reload, toast } = useStore();
  const existing = id ? branches.find((b) => b.id === id) ?? null : null;

  const [draft, setDraft] = useState<Branch | null>(existing);
  const [saving, setSaving] = useState(false);

  // Актуальная версия филиала из стора — по ней видно, привязалась ли группа
  // (список перезагружается, пока открыт код привязки).
  const live = id ? branches.find((b) => b.id === id) ?? null : null;
  const tgLinked = !!live?.telegram_linked;

  if (!draft) {
    return (
      <Modal open onClose={onClose} title="Филиал" size="md"
        footer={<button className="btn-ghost" onClick={onClose}>Закрыть</button>}>
        <p className="text-sm text-ink-3">
          Филиал не найден. Новый филиал подключается на экране «Филиалы» — там показаны города из Битрикса.
        </p>
      </Modal>
    );
  }

  const valid = draft.name.trim().length > 0;
  // Пояс филиала = смещение от UTC. Список строим вокруг текущего значения, чтобы
  // нестандартное смещение (получасовой пояс) не подменилось молча на соседнее.
  const tzOptions = tzOptionsWith(draft.tz_offset_minutes);

  const save = async () => {
    if (saving || !valid) return;
    setSaving(true);
    try {
      await saveBranch(draft);
      onClose();
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось сохранить' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open onClose={onClose} title="Филиал" size="md"
      footer={<>
        <DeleteBranchButton branch={draft} onDone={onClose} />
        <button className="btn-ghost" onClick={onClose}>Отмена</button>
        <button className="btn-primary" disabled={!valid || saving} onClick={() => void save()}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Сохранить
        </button>
      </>}
    >
      <div className="space-y-3">
        <div>
          <label className="field-label">Название*</label>
          <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} autoFocus />
        </div>
        <div>
          <label className="field-label">Город</label>
          <input className="input" value={draft.city ?? ''} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
        </div>
        <div>
          <label className="field-label">Цвет</label>
          <div className="flex flex-wrap gap-1.5">
            {PALETTE.map((c) => (
              <button key={c} type="button" onClick={() => setDraft({ ...draft, color: c })}
                className={cn('w-7 h-7 rounded-full', draft.color.toLowerCase() === c.toLowerCase() && 'ring-2 ring-ink-1 ring-offset-2')}
                style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>
        <div>
          <label className="field-label">Часовой пояс</label>
          {/* Смещение от UTC — по нему считают время часы в шапке, календарь и бот.
              Именованную зону (timezone) выводит сервер из смещения — здесь её не трогаем. */}
          <select
            className="input"
            value={String(draft.tz_offset_minutes)}
            onChange={(e) => setDraft({ ...draft, tz_offset_minutes: Number(e.target.value) })}
          >
            {tzOptions.map((t) => <option key={t.offsetMin} value={t.offsetMin}>{t.label}</option>)}
          </select>
        </div>

        {/* Связь с Битриксом — только показываем, руками её не меняют. */}
        <div className="flex items-center gap-2 text-[12px]">
          {draft.bitrix_enum_id ? (
            <span className="chip !text-status-paid !bg-status-paid/12"><Check className="w-3 h-3" /> Город из Битрикса подключён</span>
          ) : (
            <span className="chip !text-status-refused !bg-status-refused/12">Не связан с Битриксом</span>
          )}
        </div>

        <TelegramBlock branchId={draft.id} linked={tgLinked} onChanged={() => void reload()} />
      </div>
    </Modal>
  );
}

/** Кнопка удаления: если к филиалу привязаны игры — сервер отвечает 409, спрашиваем и повторяем. */
function DeleteBranchButton({ branch, onDone }: { branch: Branch; onDone: () => void }) {
  const { reload, toast, setModal } = useStore();
  const [busy, setBusy] = useState(false);

  const run = async (force: boolean) => {
    setBusy(true);
    try {
      await apiDeleteBranch(branch.id, force);
      await reload();
      toast({ kind: 'success', text: `Филиал «${branch.name}» удалён` });
      onDone();
    } catch (e) {
      if (e instanceof BranchHasDealsError) {
        // Предупреждаем понятным языком и даём удалить всё равно.
        setModal({
          kind: 'confirm',
          title: 'Удалить филиал?',
          message: `К филиалу привязано ${e.dealsCount} ${plural(e.dealsCount, 'игра', 'игры', 'игр')}. `
            + 'Если удалить, они останутся без филиала и пропадут из календарей городов. Всё равно удалить?',
          danger: true,
          onConfirm: () => { void run(true); },
        });
        return;
      }
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось удалить филиал' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button className="btn-ghost text-status-refused mr-auto" disabled={busy} onClick={() => void run(false)}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Удалить
    </button>
  );
}

/**
 * Блок «Telegram-группа»: код привязки (действует 10 минут) + команда, которую
 * нужно отправить в рабочей группе филиала.
 */
function TelegramBlock({ branchId, linked, onChanged }: { branchId: string; linked: boolean; onChanged: () => void }) {
  const toast = useStore((s) => s.toast);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState<TelegramCode | null>(null);
  const [left, setLeft] = useState(0); // сколько секунд ещё живёт код
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  // Пока показан код — раз в 4 секунды перечитываем филиалы: как только бот
  // привяжет группу, статус в карточке сменится сам.
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  useEffect(() => {
    if (!code || linked) return;
    const t = window.setInterval(() => onChangedRef.current(), 4000);
    return () => window.clearInterval(t);
  }, [code, linked]);

  // Обратный отсчёт жизни кода.
  useEffect(() => {
    if (!code) return;
    const tick = () => {
      const ms = new Date(code.expires_at).getTime() - Date.now();
      setLeft(Math.max(0, Math.floor(ms / 1000)));
    };
    tick();
    const t = window.setInterval(tick, 1000);
    return () => window.clearInterval(t);
  }, [code]);

  // Привязалась — код больше не нужен.
  useEffect(() => { if (linked) setCode(null); }, [linked]);

  const request = async () => {
    if (busy) return;
    setBusy(true);
    try {
      setCode(await requestBranchTelegramCode(branchId));
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось получить код' });
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await unlinkBranchTelegram(branchId);
      onChangedRef.current();
      toast({ kind: 'success', text: 'Группа отвязана' });
      setConfirmUnlink(false);
    } catch (e) {
      toast({ kind: 'error', text: e instanceof Error ? e.message : 'Не удалось отвязать группу' });
    } finally {
      setBusy(false);
    }
  };

  const command = code ? `/bind ${code.code}` : '';
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      toast({ kind: 'success', text: 'Команда скопирована' });
    } catch {
      toast({ kind: 'error', text: 'Скопируйте команду вручную' });
    }
  };

  return (
    <div className="rounded-xl border border-line p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-ink-2">
        <Send className="w-4 h-4 text-accent" /> Telegram-группа
      </div>

      {linked ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2.5 rounded-xl px-3.5 py-3 bg-status-paid/10 border border-status-paid/25">
            <span className="w-7 h-7 rounded-full grid place-items-center bg-status-paid/20 text-status-paid flex-shrink-0">
              <Check className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-status-paid">Группа привязана</div>
              <div className="text-[12px] text-ink-3">Бот присылает в неё сводку игр филиала.</div>
            </div>
          </div>
          {confirmUnlink ? (
            <div className="flex flex-wrap gap-2">
              <button className="btn-danger !justify-center" onClick={() => void unlink()} disabled={busy}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} Точно отвязать?
              </button>
              <button className="btn-ghost" onClick={() => setConfirmUnlink(false)} disabled={busy}>Отмена</button>
            </div>
          ) : (
            <button className="btn-soft !justify-center text-status-refused" onClick={() => setConfirmUnlink(true)}>
              <X className="w-4 h-4" /> Отвязать
            </button>
          )}
        </div>
      ) : code ? (
        <div className="space-y-3">
          {/* Крупный код — его вписывают в группе. */}
          <div className="rounded-xl bg-surface-2 border border-line px-4 py-3 text-center">
            <div className="text-[11px] uppercase tracking-wider text-ink-3 font-semibold">Код привязки</div>
            <div className="text-3xl font-black tracking-[0.2em] text-ink-1 tabular-nums mt-1">{code.code}</div>
            <div className="text-[12px] text-ink-3 mt-1">
              {left > 0 ? `Действует ещё ${fmtLeft(left)}` : 'Код истёк — получите новый'}
            </div>
          </div>

          <ol className="text-[13px] text-ink-2 space-y-1.5 list-decimal pl-5">
            <li>Добавьте бота <b>@{code.bot_username}</b> в рабочую группу филиала.</li>
            <li>Напишите в группе: <code className="px-1.5 py-0.5 rounded bg-surface-2 font-semibold">{command}</code></li>
            <li>Бот ответит, что группа привязана.</li>
          </ol>

          <div className="flex flex-wrap gap-2">
            <button className="btn-soft" onClick={() => void copy()}>
              <Copy className="w-4 h-4" /> Скопировать команду
            </button>
            <button className="btn-ghost" onClick={() => onChangedRef.current()}>
              Я привязал — проверить
            </button>
            {left <= 0 && (
              <button className="btn-primary" onClick={() => void request()} disabled={busy}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Новый код
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[13px] text-ink-3">
            Привяжите рабочую группу филиала — бот будет присылать в неё сводку игр на день.
          </p>
          <button className="btn-primary" onClick={() => void request()} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Привязать группу
          </button>
        </div>
      )}
    </div>
  );
}

function fmtLeft(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** «1 игра / 2 игры / 5 игр». */
function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}
